import './heroj.css';
import { reproducir, detener } from '../player/player.js';
import { posterDe } from '../../config/videos.js';

// HERO "Julio · Revelado" + entrada al recorrido 3D.
// Fuente: "Flujo Método 3D - Storyboard" (Claude Design, proyecto dd25dfb7),
// que sustituyó la transición de tinta por una MÁS SENCILLA.
//
// La tinta (máscara SVG animada por frame) se fue: costaba un filtro completo
// —turbulencia + dos desplazamientos— recalculado en cada frame, y competía con
// el render 3D en el mismo presupuesto. Ahora la frontera es un DESLIZAMIENTO:
// el hero sube y su borde inferior barre la pantalla de abajo hacia arriba,
// dejando ver el canvas. Es un solo `transform` (compositado por GPU) más una
// máscara ESTÁTICA de 130px que suaviza ese borde. Coste ~nulo.
//
// Del hero se conserva el revelado por cursor, que es su identidad: el rastro
// abre huecos por los que asoma un SEGUNDO retrato (el de blanco y negro).

// EL GRUPO DEL HERO: Julio al frente y el equipo detrás, como un retrato de
// grupo de verdad.
//
// LO QUE ESTABA MAL. Los compañeros iban tendidos en diagonal por la mitad
// superior, a opacidad 0.3 y con la foto de Julio a otra escala. No leían como
// "detrás de él": leían como cuatro fantasmas sueltos. La profundidad se
// intentaba con OPACIDAD, y la opacidad no da profundidad: da transparencia.
//
// CÓMO SE ARMA AHORA. La referencia que pasó Julio es un retrato de grupo
// clásico, y al medirlo salta lo importante: LAS CUATRO CABEZAS MIDEN CASI LO
// MISMO. Quien está al frente no es más grande, es el que TAPA a los demás. La
// profundidad la da el traslape, no la escala.
//
// Por eso cada retrato se coloca por su CABEZA y no por su marco:
//   --cab   ancho del cráneo respecto al ancho de la imagen
//   --arr   dónde empieza la cabeza, respecto al alto de la imagen
//   --cx    centro horizontal de la cabeza, respecto al ancho de la imagen
// Los tres salen de medir el alfa de cada archivo (no se estimaron), y con ellos
// el CSS resuelve el tamaño y la posición para que todas las cabezas queden del
// mismo tamaño y a la altura que se le pida. Cambiar de foto es volver a medir
// esas tres cifras: la composición no se toca.
//
// --hx / --hy son el destino: dónde cae la cabeza dentro del panel.

// De los archivos `busto-*`, no de los `recorte-*`. Los recortes que llegaron de
// la landing sólo están recortados alrededor de la cabeza: de medio pecho hacia
// abajo el alfa es un rectángulo opaco —el fondo original de la foto—, al 60%
// de la altura en Alexi, al 64% en Johan y al 79% en Manuel. Sobre el blanco de
// la landing no se ve; sobre este fondo cada quien salía metido en una caja
// gris. `npm run busto` los corta de verdad y los desvanece (ver tools/busto.mjs).
// EL FONDO: los tres van más apagados que antes porque encima les cae la capa
// azul, y una foto a plena luz debajo del azul se lee sucia, no teñida.
// Las tres medidas de cada uno (cab/arr/cx) salen de MEDIR el alfa de su
// archivo, nunca de estimarlas: por eso al cambiar una foto hay que volver a
// medir. La de Manuel cambió de 0.424 a 0.350 al llegar su retrato nuevo —está
// más lejos en el encuadre—, y si se hubiera dejado el número viejo su cabeza
// habría salido un 20% más grande que las demás, que es justo lo que rompe el
// retrato de grupo.
const ATRAS = [
  { src: '/busto-johan-rojas.webp',           cab: 0.479, arr: 0.168, cx: 0.511, hx: 25, hy: 21.0, luz: 0.72, velo: 1.0 },
  { src: '/busto-manuel-suarez-barraza.webp', cab: 0.350, arr: 0.128, cx: 0.506, hx: 42, hy: 17.5, luz: 0.76, velo: 0.7 },
  { src: '/busto-carlos-zamora.webp',         cab: 0.560, arr: 0.115, cx: 0.396, hx: 76, hy: 16.5, luz: 0.78, velo: 0.9 },
];

// EL FRENTE. Julio se pinta DESPUÉS de la capa azul, y ahí está todo el efecto:
// el azul no lo alcanza. No es más grande que los otros tres —eso rompería el
// retrato de grupo—; es el único a color de verdad, rodeado de gente teñida. Esa
// es la jerarquía, y no cuesta un solo píxel de tamaño.
const FRENTE = { src: '/busto-julio-pasos.webp', cab: 0.369, arr: 0.204, cx: 0.487, hx: 60, hy: 21.0, luz: 1, velo: 0 };

// El avatar del modal. NO es el busto: dentro de un círculo de 34 px un recorte
// con fondo transparente deja ver el panel por detrás de la cabeza y se lee como
// un error. Va la foto cuadrada, la misma que usan las fichas de instructor.
const AVATAR = '/julio-pasos.webp';

// `aria-hidden`: son decoración. A quien usa lector de pantalla ya se le nombra
// a los instructores en su página; repetirlos aquí solo estorba.
//
// NINGUNA VA EN `lazy`: las cuatro están arriba del pliegue. Marcarlas así solo
// retrasa el primer pintado del hero y hace que el grupo aparezca por partes.
// Lo que sí se ordena es la PRIORIDAD: Julio alto, el resto bajo.
const cara = (p, prio) => `<img class="zk-hj__cara" src="${p.src}" alt="" decoding="async"
          fetchpriority="${prio}"
          style="--cab:${p.cab};--arr:${p.arr};--cx:${p.cx};--hx:${p.hx}%;--hy:${p.hy}%;--luz:${p.luz};--velo:${p.velo}px" />`;

// TRES CAPAS, y el orden ES el diseño: el equipo, encima el azul, encima Julio.
// Antes el azul era un `::after` del contenedor, y un `::after` siempre pinta al
// final: teñía a los cuatro por igual. Convertido en un elemento de verdad se
// puede meter EN MEDIO, que es lo único que hacía falta.
const GRUPO_HTML = `
      <div class="zk-hj__grupo" aria-hidden="true">
        ${ATRAS.map((p) => cara(p, 'low')).join('')}
      </div>
      <div class="zk-hj__azul" aria-hidden="true"></div>
      <div class="zk-hj__grupo zk-hj__grupo--frente" aria-hidden="true">
        ${cara(FRENTE, 'high')}
      </div>`;

// Modal "Conoce a Julio": es lo que ABSORBE la antigua sección de Julio Pasos
// del homepage. Comparte con ella la MISMA pieza —clave 'julio-historia' en
// src/config/videos.js—, así que las dos se actualizan de un solo lugar. El
// reproductor se crea al abrir y se destruye al cerrar, para que el audio no
// siga sonando detrás.
// INTERRUPTOR de la historia de Julio. En falso no se dibuja la placa del
// hero ni su modal, y el hero queda con los dos botones y nada más. Está
// apagada mientras se graba el video nuevo: la pieza de hoy es un enlace de
// YouTube provisional y la placa promete "su historia en 2 min" sin tenerla.
// Para encenderla otra vez basta poner `true` — no hay nada más que tocar.
const HISTORIA = false;

const STORY_POSTER = posterDe('julio-historia');
const STORY_STATS = [
  { n: '+25', l: 'años en la<br>industria' },
  { n: '97', l: 'plantas como<br>Director Global' },
  { n: '+500', l: 'eventos Kaizen<br>facilitados' },
];

const REVEAL_RADIUS = 190;
// Velocidad a la que se cierra el rastro (y por tanto vuelve el color). Vida de
// cada gota, en fracción por frame: 0.030 ≈ 0.55 s a 60 fps (el diseño traía
// 0.012, casi 1.4 s, y se sentía pesado).
// Más rápido que antes (0.030): los puntos mueren en ~24 fotogramas en vez de
// ~33, así hay menos vivos a la vez. Es la otra mitad del ahorro, y además el
// rastro se limpia antes, que es lo que se pidió.
const TRAIL_DECAY = 0.042;
// Curva de desvanecido. Con exponente <1 la gota aguanta opaca y se apaga de
// golpe al final —justo lo que hacía que "tardara". Por encima de 1 empieza a
// irse desde el primer momento, así que el rastro se lee como tinta que se
// seca y no como una mancha que espera. Subirlo lo hace aún más fugaz.
const TRAIL_FADE = 1.5;
// PRESUPUESTO DEL RASTRO. Cada punto es un círculo con degradado, de hasta
// ~250 px de radio, DENTRO de una máscara SVG que el navegador vuelve a
// rasterizar en cada fotograma sobre todo el panel. 70 de esos era lo que hacía
// que mover el mouse se sintiera pesado: no es el JS, es el relleno.
// A 34 el rastro se sigue leyendo continuo —con MIN_GAP de 34 px y hasta 3
// puntos por movimiento, 34 cubren de sobra el trazo visible— y el costo de
// relleno por fotograma baja a menos de la mitad.
const MAXP = 34;
const MIN_GAP = 34;

const ARROW = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12h14M13 6l6 6-6 6"/></svg>`;
const DOWN = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>`;

// EL TITULAR GIGANTE, de "Hero Zenkai v2 · Titular gigante" (Claude Design).
//
// Los renglones se cortan A MANO porque el corte ES el diseño: decide qué palabra
// se queda sola. La luz cruza el titular ENTERO, así que ningún renglón lleva
// trato aparte —`hi`, que marcaba el renglón del degradado, ya no se usa y se
// conserva solo como apunte del diseño original.
//
// Se conserva la tabla COMPLETA de frases del diseño —el panel de Tweaks las
// ofrecía como alternativas— para que cambiar de lema sea mover una sola palabra
// en FRASE y no volver a escribir el titular:
//   'Ser bueno ya no basta' · 'Lo técnico no te asciende'
//   'De ingeniero a gerente, rápido' · 'Deja de operar. Empieza a dirigir'
//   'Ingeniero hoy. Gerente mañana' · 'Tu título no te hace gerente'
const TITULARES = {
  'De ingeniero a gerente':            { lineas: ['De ingeniero', 'a gerente'], pie: 'en hasta la mitad del tiempo' },
  'Ser bueno ya no basta':             { lineas: ['Ser', 'bueno', 'ya no', 'basta'], hi: 3 },
  'Lo técnico no te asciende':         { lineas: ['Lo técnico', 'no te', 'asciende'], hi: 2 },
  'De ingeniero a gerente, rápido':    { lineas: ['De ingeniero', 'a gerente,', 'rápido'], hi: 1 },
  'Deja de operar. Empieza a dirigir': { lineas: ['Deja de operar.', 'Empieza', 'a dirigir'], hi: 2 },
  'Ingeniero hoy. Gerente mañana':     { lineas: ['Ingeniero hoy.', 'Gerente', 'mañana'], hi: 1 },
  'Tu título no te hace gerente':      { lineas: ['Tu título', 'no te hace', 'gerente'], hi: 2 },
};
// `pie` es la coletilla en cursiva bajo el titular. Va APARTE del titular y no
// como un tercer renglón: es otra voz —minúscula, cursiva, gris— y si entrara en
// el h1 se la llevaría el recorte al texto y saldría con el degradado encima.
const FRASE = 'De ingeniero a gerente';
const TITULAR = TITULARES[FRASE];

// La bajada, también del handoff y literal.
const BAJADA = 'Transformamos ingenieros técnicamente buenos en líderes que llegan a la gerencia, ganan lo que valen y le dan una mejor vida a su familia.';

function markup() {
  return `

  <div class="zk-hj__group">
    <!-- El grupo se prolonga 130px por debajo del escenario: ahí vive el borde
         difuminado, fuera de pantalla mientras el hero está en reposo.
         Las capas VISUALES (fondo, retrato, scrims, puntos) llegan hasta abajo,
         para que el desvanecido ocurra sobre la imagen y no sobre una franja
         vacía. Solo la UI anclada —viga, copy, placa, pista— va en __inner, que
         mide exactamente el escenario y así no se descoloca. -->
    <div class="zk-hj__bg" aria-hidden="true"></div>

    <div class="zk-hj__art">${GRUPO_HTML}</div>

    <!-- LA LUZ DEL CURSOR es un <canvas>, no una máscara SVG. La máscara
         obligaba a re-rasterizar TODO lo enmascarado en cada movimiento del
         mouse; medido en la máquina de Julio: 45 fps. El canvas es una capa
         propia, se compone en GPU y el DOM no se vuelve a pintar. -->
    <canvas class="zk-hj__luz" aria-hidden="true"></canvas>

    <!-- Velo + viñeta + puntos, EN UNA SOLA CAPA (tres fondos apilados). Eran
         tres elementos a pantalla completa componiéndose por fotograma; ver la
         nota en heroj.css. Visualmente es idéntico. -->
    <div class="zk-hj__scrim" aria-hidden="true"></div>

    <div class="zk-hj__inner">
    <div class="zk-hj__beam" aria-hidden="true"></div>

    <!-- La variable --hj-lineas la lee el CSS para calcular cuánto puede medir
         el titular sin chocar con el nav ni con el pie. Iba como constante
         derivada de CUATRO renglones, así que al pasar a dos se quedaba pequeño
         sin motivo —y al revés habría desbordado sin avisar—. -->
    <div class="zk-hj__copy" style="--hj-lineas:${TITULAR.lineas.length}">
      <div class="zk-hj__eyebrow">Conoce el método Zenkai</div>
      <h1 class="zk-hj__h1">${TITULAR.lineas.map((t) => `<span>${t}</span>`).join('')}</h1>
      ${TITULAR.pie ? `<p class="zk-hj__pie">${TITULAR.pie}</p>` : ''}
      <p class="zk-hj__lead">${BAJADA}</p>
      <div class="zk-hj__cta">
        <a class="zk-hj__btn" href="#pricing">Únete a Zenkai${ARROW}</a>
        <!-- Baja a "¿Qué es Zenkai?", la sección de abajo: antes mandaba al
             catálogo en /especializate, que es el final del recorrido y no el
             principio. El id real de esa sección es que-es-zenkai (ver
             mountQuees). El salto lo suaviza el manejador de más abajo. -->
        <a class="zk-hj__btn2" href="#que-es-zenkai">Descubre Zenkai${DOWN}</a>
      </div>
    </div>

    <!-- La placa de autor ES el disparador de su historia. Una pieza, no dos:
         queda lejos del nav y la invitación cae sobre la persona de la que
         habla. Antes eran una píldora arriba + una placa abajo. -->
    ${HISTORIA ? `<button type="button" class="zk-hj__plate">
      <span class="ico"><svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true"><path d="M0 0l10 6-10 6z"/></svg></span>
      <span class="txt">
        <span class="n">Julio Pasos</span>
        <span class="r">Tardó 16 años en su primera gerencia · su historia en <span class="t">2 min</span></span>
      </span>
    </button>` : ''}

    <div class="zk-hj__cue">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>
      <span>Baja para entrar</span>
    </div>

    </div>
  </div>

  <!-- El modal vive FUERA del grupo: no lo recorta la máscara ni se va con el
       deslizamiento. Absorbe lo que antes era la sección "Julio Pasos". -->
  ${HISTORIA ? `<div class="zk-hj__modal" data-open="false" role="dialog" aria-modal="true" aria-label="Conoce a Julio">
    <div class="zk-hj__panel">
      <div class="zk-hj__panel-bar" data-st>
        <span class="ttl">Conoce a Julio</span>
        <button type="button" class="zk-hj__close" aria-label="Cerrar"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      </div>
      <div class="zk-hj__panel-grid">
        <div class="zk-hj__video" data-st style="--d:60ms" role="button" tabindex="0" aria-label="Reproducir la historia de Julio">
          <img class="zk-hj__video-poster" alt="" />
          <span class="zk-hj__video-scrim"></span>
          <span class="zk-hj__video-play"><svg width="20" height="23" viewBox="0 0 20 23" aria-hidden="true"><path d="M0 0l20 11.5L0 23z"/></svg></span>
          <span class="zk-hj__video-len">2:04</span>
        </div>

        <div class="zk-hj__story">
          <p class="zk-hj__hook" data-st style="--d:110ms">Tardé <em>16 años</em> en llegar a mi primera gerencia. Me faltaban ejes.</p>
          <p class="zk-hj__body" data-st style="--d:160ms">Era técnicamente bueno, pero me quedaba callado en las juntas y no sabía venderme. Cambié de empresa durante 10 años sin resolver el fondo. Cuando trabajé lo que me faltaba, en 6 años pasé de mi primera gerencia a director global.</p>
          <div class="zk-hj__author" data-st style="--d:210ms">
            <span class="av"><img src="${AVATAR}" alt="" /></span>
            <div>
              <div class="n">Julio Pasos</div>
              <div class="r">Autor de <span>“Conviértete en Inge Chingón”</span></div>
            </div>
          </div>
          <div class="zk-hj__stats" data-st style="--d:260ms">
            ${STORY_STATS.map((s) => `<div><div class="n">${s.n}</div><div class="l">${s.l}</div></div>`).join('')}
          </div>
        </div>
      </div>
    </div>
  </div>` : ''}`;
}

/**
 * Monta el hero DENTRO del escenario del recorrido 3D: al deslizarse hacia
 * arriba deja ver el canvas vivo que ya está detrás.
 *
 * @param {HTMLElement} stage `.zk-stage` (contiene el canvas).
 * @param {object} opts
 * @param {HTMLElement} opts.rail  riel de scroll compartido.
 * @param {number} opts.leadPx     píxeles que dura la fase del hero.
 * @param {HTMLElement} [opts.overlay] overlay del recorrido: emerge al final.
 */
export function mountHeroJulio(stage, opts) {
  const { rail, leadPx, overlay } = opts;
  const host = document.createElement('div');
  host.className = 'zk-hj';
  host.innerHTML = markup();
  stage.appendChild(host);

  const group = host.querySelector('.zk-hj__group');
  // El canvas de la luz existe siempre (está en el markup); lo usa el bloque
  // del cursor para dibujar y `paint()` para apagarlo cuando el hero se va.
  const luz = host.querySelector('.zk-hj__luz');

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse = window.matchMedia('(pointer: coarse)').matches;

  // ---- La luz del cursor (identidad del hero) ----------------------------
  //
  // ANTES: 34 círculos SVG con degradado radial dentro de un <mask>, y debajo
  // dos capas de degradados con modos de mezcla. Cada movimiento del mouse
  // cambiaba la máscara y el navegador volvía a rasterizar todo lo enmascarado
  // (~1000×1300 px) en cada fotograma. Medido: 45 fps con el mouse encima.
  //
  // AHORA: los mismos círculos, la misma tinta, pero pintados en un <canvas>:
  //   · el DISCO es un sprite de 256 px con las mismas paradas que tenía el
  //     radialGradient de la máscara; se dibuja escalado, no se recalcula.
  //   · la TINTA —los cinco degradados que tenía .zk-hj__photo-tint y su
  //     grano— se pinta UNA vez en un canvas aparte, al medir.
  //   · por fotograma: se dibujan los discos y se les mete la tinta con
  //     `source-in`. Eso es todo. La mezcla `screen` la hace el compositor,
  //     porque el canvas es una capa propia.
  if (!coarse && !reduced) {
    host.classList.add('has-cursor');
    const ctx = luz.getContext('2d');
    const DPR_LUZ = 1.5;   // la luz es difusa: más densidad no se ve, sí se paga

    // El disco: paradas idénticas al #hj-soft que había en el SVG.
    const SP = 256;
    const disco = document.createElement('canvas');
    disco.width = disco.height = SP;
    {
      const g = disco.getContext('2d');
      const gr = g.createRadialGradient(SP / 2, SP / 2, 0, SP / 2, SP / 2, SP / 2);
      gr.addColorStop(0, 'rgba(255,255,255,1)');
      gr.addColorStop(0.52, 'rgba(255,255,255,1)');
      gr.addColorStop(0.78, 'rgba(255,255,255,0.62)');
      gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr;
      g.fillRect(0, 0, SP, SP);
    }

    // El grano: la misma turbulencia SVG que llevaba la capa de antes. Se carga
    // como imagen y se estampa sobre la tinta con `overlay`, una sola vez.
    const grano = new Image();
    grano.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E";

    const tinta = document.createElement('canvas');
    let W = 0, H = 0, dpr = 1;

    // Los cinco degradados de la tinta, tal como estaban en el CSS. Van sobre
    // el PANEL del retrato (--hj-ancho, anclado a la derecha), no sobre todo el
    // escenario. Se pintan en orden inverso al CSS: ahí el primero queda arriba.
    const pintarTinta = () => {
      const g = tinta.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);
      const pct = parseFloat(getComputedStyle(group).getPropertyValue('--hj-ancho')) || 56;
      const pw = W * pct / 100, px = W - pw, ph = H;
      g.save();
      g.translate(px, 0);
      g.beginPath(); g.rect(0, 0, pw, ph); g.clip();

      // linear-gradient(150deg, …): el eje del degradado como lo define CSS.
      const ang = 150 * Math.PI / 180, dx = Math.sin(ang), dy = -Math.cos(ang);
      const L = pw * Math.abs(dx) + ph * Math.abs(dy);
      const lg = g.createLinearGradient(pw / 2 - dx * L / 2, ph / 2 - dy * L / 2, pw / 2 + dx * L / 2, ph / 2 + dy * L / 2);
      lg.addColorStop(0, 'rgba(166,38,57,0.34)');
      lg.addColorStop(0.40, 'rgba(196,73,31,0.22)');
      lg.addColorStop(1, 'rgba(4,85,191,0.40)');
      g.fillStyle = lg; g.fillRect(0, 0, pw, ph);

      // radial-gradient(RX RY at CX CY, color, transparent FIN)
      const radial = (rx, ry, cx, cy, color, fin) => {
        g.save();
        g.translate(cx * pw, cy * ph);
        g.scale(rx * pw, ry * ph);
        const rg = g.createRadialGradient(0, 0, 0, 0, 0, 1);
        rg.addColorStop(0, color);
        rg.addColorStop(fin, 'rgba(0,0,0,0)');
        g.fillStyle = rg;
        g.fillRect(-cx / rx, -cy / ry, 1 / rx, 1 / ry);
        g.restore();
      };
      radial(0.74, 0.60, 0.64, 1.06, 'rgba(9,129,255,0.85)', 0.70);
      radial(0.50, 0.46, 0.08, 0.94, 'rgba(30,209,233,0.58)', 0.66);
      radial(0.42, 0.44, 0.98, 0.34, 'rgba(255,132,0,0.62)', 0.64);
      radial(0.44, 0.48, 0.06, 0.10, 'rgba(212,50,60,0.80)', 0.66);

      if (grano.complete && grano.naturalWidth) {
        g.globalCompositeOperation = 'overlay';
        g.globalAlpha = 0.22;
        g.fillStyle = g.createPattern(grano, 'repeat');
        g.fillRect(0, 0, pw, ph);
      }
      g.restore();
    };

    const medir = () => {
      const r = stage.getBoundingClientRect();
      W = r.width; H = r.height;
      dpr = Math.min(window.devicePixelRatio || 1, DPR_LUZ);
      luz.width = tinta.width = Math.round(W * dpr);
      luz.height = tinta.height = Math.round(H * dpr);
      // El tamaño en CSS se fija aquí y no con `inset: 0`: el grupo mide 130 px
      // más que el escenario, y el canvas se habría estirado en vertical.
      luz.style.width = `${W}px`;
      luz.style.height = `${H}px`;
      pintarTinta();
    };
    medir();
    grano.addEventListener('load', pintarTinta);
    window.addEventListener('resize', medir, { passive: true });

    const pts = [];
    let lastX = -1e5, lastY = -1e5, raf = 0, hover = false;

    const push = (x, y) => {
      if (pts.length >= MAXP) {
        let old = 0;
        for (let i = 1; i < pts.length; i++) if (pts[i].life < pts[old].life) old = i;
        pts.splice(old, 1);
      }
      const a = Math.random() * Math.PI * 2;
      pts.push({
        x: x + (Math.random() - 0.5) * 26, y: y + (Math.random() - 0.5) * 26,
        dx: Math.cos(a) * (8 + Math.random() * 14), dy: Math.sin(a) * (8 + Math.random() * 14),
        r0: REVEAL_RADIUS * (0.7 + Math.random() * 0.66), life: 1,
      });
    };
    const seed = (x, y) => {
      const dx = x - lastX, dy = y - lastY;
      const d = Math.hypot(dx, dy);
      if (d < MIN_GAP) return;
      if (lastX < -1e4 || d > MIN_GAP * 14) push(x, y);
      else {
        const steps = Math.min(3, Math.round(d / MIN_GAP));
        for (let s = 1; s <= steps; s++) push(lastX + dx * (s / steps), lastY + dy * (s / steps));
      }
      lastX = x; lastY = y;
    };
    const frame = () => {
      raf = 0;
      for (let i = pts.length - 1; i >= 0; i--) {
        pts[i].life -= TRAIL_DECAY;
        if (pts[i].life <= 0) pts.splice(i, 1);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < pts.length; i++) {
        const pt = pts[i], t = 1 - pt.life;
        const r = pt.r0 * (1 + t * 0.9);
        ctx.globalAlpha = Math.pow(pt.life, TRAIL_FADE);
        ctx.drawImage(disco, pt.x + pt.dx * t - r, pt.y + pt.dy * t - r, r * 2, r * 2);
      }
      // Los discos son la forma; la tinta entra solo donde hay disco.
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-in';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(tinta, 0, 0);
      if (pts.length) raf = requestAnimationFrame(frame);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(frame); };

    stage.addEventListener('mousemove', (e) => {
      if (host.classList.contains('is-gone')) return;
      // Si se midió con el escenario todavía sin tamaño (pestaña en segundo
      // plano al cargar), el canvas quedó en 0×0: se vuelve a medir aquí, que
      // es la primera vez que de verdad hace falta.
      if (!W || !H) medir();
      const b = stage.getBoundingClientRect();
      const x = e.clientX - b.left, y = e.clientY - b.top;
      if (!hover) { hover = true; lastX = lastY = -1e5; }
      seed(x, y);
      kick();
    });
    stage.addEventListener('mouseleave', () => { hover = false; kick(); });
  }

  // ---- Transición: el hero se desliza y descubre el canvas ---------------
  const copy = host.querySelector('.zk-hj__copy');
  const plate = host.querySelector('.zk-hj__plate');
  const cue = host.querySelector('.zk-hj__cue');
  const art = host.querySelector('.zk-hj__art');
  const beam = host.querySelector('.zk-hj__beam');

  const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const ss = (v) => { const x = clamp(v); return x * x * (3 - 2 * x); };

  let p = 0;
  let lastTrailOff = null;
  const paint = () => {
    // LA transición: un solo transform. El borde inferior del grupo (suavizado
    // con una máscara estática de 130px) barre la pantalla hacia arriba.
    group.style.transform = `translateY(${(-ss(p) * 100).toFixed(2)}%)`;
    // Mientras el grupo esté en su sitio, el canvas 3D de abajo está tapado por
    // completo (`.zk-hj__bg` es opaco). Se avisa al recorrido para que no gaste
    // GPU dibujando lo que nadie ve: medido, ese render competía con la luz
    // del cursor y le costaba 10 fps.
    //
    // El umbral mira lo DESTAPADO, no el scroll. Con `p < 0.002` el recorrido
    // arrancaba a componerse —escena entera, con N8AO— en cuanto el hero se
    // movía un pelo, cuando todavía lo tapaba casi por completo: el grupo se
    // desplaza `ss(p)` de su alto, así que a p = 0.05 había destapados unos
    // 13 px de 900. Con `ss(p) < 0.03` (≈ p < 0.10) la tira no llega al 3.5% del
    // alto, que no se distingue, y hasta ahí no se compone nada.
    // Congelar ahí no pierde animación del recorrido: `getProgress()` devuelve 0
    // durante TODA la fase del hero, así que la escena no avanza igualmente.
    stage.classList.toggle('is-covered', ss(p) < 0.03);

    // El contenido se retira antes de que el grupo termine de salir.
    const out = ss(p / 0.46);
    copy.style.opacity = (1 - out).toFixed(3);
    copy.style.transform = `translateY(calc(-50% - ${(out * 26).toFixed(1)}px))`;
    // La placa es un botón: al desvanecerse hay que quitarle también los
    // clics, o quedaría un disparador invisible capturando el cursor.
    if (plate) {
      const plateOp = 1 - ss(p / 0.32);
      plate.style.opacity = plateOp.toFixed(3);
      plate.style.pointerEvents = plateOp > 0.05 ? 'auto' : 'none';
    }
    cue.style.opacity = (1 - ss(p / 0.20)).toFixed(3);
    art.style.transform = `scale(${(1 + out * 0.05).toFixed(4)})`;
    beam.style.opacity = (1 - out).toFixed(3);
    // CONTRAMOVIMIENTO. El grupo entero sube una pantalla al salir, y hasta
    // ahora TODAS sus capas viajaban a 1:1 con él —incluido este resplandor de
    // fondo—. Un fondo que se mueve igual que el primer plano es, literalmente,
    // lo contrario de la profundidad: delata que es un recorte plano.
    // La viga cede 100 px hacia abajo mientras el grupo sube, así que su
    // recorrido neto es menor y se lee más lejos. 100 px es el tope que marca la
    // regla para desplazamiento ligado al scroll; por encima se nota el truco.
    // Es una escritura de transform por fotograma sobre un elemento que ya
    // recibía una de opacidad: no añade capa ni trabajo de maquetación.
    //
    // NO se aplica con movimiento reducido. El parallax —dos capas viajando a
    // distinta velocidad— es disparador vestibular, y aquí no basta con la
    // regla CSS porque esto lo escribe el JS. Sin él, el grupo entero viaja
    // como un solo bloque, que es justo lo que esa preferencia pide.
    if (!reduced) beam.style.transform = `translateY(${(ss(p) * 100).toFixed(1)}px)`;
    // EL VELO NO SE APAGA. Antes iba con `1 - out` y era un error visible: ese
    // velo es lo que difumina el borde izquierdo de la foto contra el fondo
    // oscuro, así que al desvanecerse aparecía un filo vertical duro y más
    // claro justo en medio del hero —"se remarca la división y se ilumina"—.
    // La viga sí se va, porque pertenece al copy y se va con él; el velo es
    // estructura de la imagen y se queda hasta que el grupo entero se desliza.

    if (overlay) overlay.style.opacity = ss((p - 0.55) / 0.35).toFixed(3);

    const gone = p > 0.99;
    host.classList.toggle('is-gone', gone);

    // Pasado el 25% la luz ya no aporta (el hero se está yendo): se apaga el
    // canvas y su mezcla `screen` deja de componerse. Esto referenciaba a
    // `blobs` —el grupo de gotas SVG que ya no existe— y lanzaba un
    // ReferenceError que cortaba la cadena de requestAnimationFrame: el hero
    // se quedaba congelado en "tapado" y el 3D, obedeciendo esa señal, en blanco.
    const trailOff = p > 0.25;
    if (trailOff !== lastTrailOff) {
      lastTrailOff = trailOff;
      luz.style.display = trailOff ? 'none' : '';
      // La luz que cruza el titular se apaga en el MISMO umbral, y por la misma
      // razón: a 0.25 el copy ya va a la mitad de su desvanecido, así que el
      // brillo no se ve y sí se paga. Animar `background-position` sobre un
      // recorte al texto REPINTA el renglón en cada fotograma —es la única
      // parte de este hero que no la compone la GPU—, de modo que dejarlo
      // corriendo mientras se scrollea competiría con el recorrido 3D justo en
      // el momento de la transición. Va en esta rama, que ya está guardada
      // contra escrituras repetidas: cuesta cero por fotograma.
      host.classList.toggle('is-quieto', trailOff);
    }
  };

  // ---- Modal "Conoce a Julio" -------------------------------------------
  // Todo el bloque cuelga de HISTORIA: con la historia apagada no existen ni
  // la placa ni el modal, y sin esta guarda el primer querySelector devuelve
  // null y el hero entero se cae al montar.
  if (HISTORIA) {
    // El panel NACE de la píldora: el transform-origin se calcula desde el rect
    // del botón, restando el del propio panel (que es su sistema de referencia).
    const modal = host.querySelector('.zk-hj__modal');
    const panel = host.querySelector('.zk-hj__panel');
    const openBtn = plate;   // la placa de autor ES el disparador
    const closeBtn = host.querySelector('.zk-hj__close');
    const videoEl = host.querySelector('.zk-hj__video');
    const videoPoster = host.querySelector('.zk-hj__video-poster');
    let mOpen = false, mTimer = 0;

    videoPoster.addEventListener('error', () => videoEl.classList.add('no-poster'));
    videoPoster.src = STORY_POSTER;

    const playStory = () => reproducir(videoEl, 'julio-historia');

    const setModal = (on) => {
      if (on === mOpen) return;
      mOpen = on;
      clearTimeout(mTimer);
      if (on) {
        modal.dataset.open = 'true';   // display primero: oculto, el rect da ceros
        const pb = panel.getBoundingClientRect();
        const bb = openBtn.getBoundingClientRect();
        panel.style.transformOrigin = `${bb.left + bb.width / 2 - pb.left}px ${bb.top + bb.height / 2 - pb.top}px`;
        // Reinicia el escalonado del contenido en cada apertura.
        modal.querySelectorAll('[data-st]').forEach((el) => { el.style.animation = 'none'; void el.offsetWidth; el.style.animation = ''; });
        closeBtn.focus();
      } else {
        modal.dataset.open = 'closing';   // la salida es más rápida que la entrada
        // Atado a `--zk-dur-exit` (150 ms) más un fotograma de margen: si se
        // acorta la salida en el DS y este número se queda, el panel pasa 30 ms
        // invisible pero ocupando la pantalla.
        mTimer = setTimeout(() => { if (!mOpen) modal.dataset.open = 'false'; }, 170);
        detener(videoEl);                 // si no, el audio sigue sonando detrás
        openBtn.focus();
      }
    };

    openBtn.addEventListener('click', () => setModal(true));
    closeBtn.addEventListener('click', () => setModal(false));
    modal.addEventListener('click', (e) => { if (e.target === modal) setModal(false); });
    videoEl.addEventListener('click', playStory);
    videoEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); playStory(); } });
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') setModal(false); });
  }

  // ---- "Descubre Zenkai" baja a la sección -------------------------------
  // MISMO patrón que usa quees.js al abrir su video, y por la misma razón:
  // `lenis.scrollTo` no mueve nada aquí —comprobado, la posición se queda en
  // cero— porque el riel del recorrido le reescribe el scroll cada frame. Se
  // para Lenis, se anima a mano y se vuelve a arrancar.
  //
  // El destino está a ~7400 px: cruza el recorrido 3D entero. La duración
  // crece con la distancia pero se topa en 1100 ms, porque más allá de eso la
  // espera se siente rota en vez de suave.
  const bajar = host.querySelector('.zk-hj__btn2');
  bajar?.addEventListener('click', (e) => {
    const destino = document.getElementById('que-es-zenkai');
    if (!destino) return;            // sin destino, que haga lo suyo el navegador
    e.preventDefault();

    const meta = () => window.pageYOffset + destino.getBoundingClientRect().top;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, meta());
      return;
    }

    const lenis = window.__zkLenis;
    if (lenis) lenis.stop();
    const desde = window.pageYOffset;
    const dur = Math.min(1100, 420 + Math.abs(meta() - desde) * 0.09);
    const t0 = performance.now();
    const paso = (ahora) => {
      const p = Math.min(1, (ahora - t0) / dur);
      const e2 = 1 - Math.pow(1 - p, 3);          // mismo ease que quees.js
      window.scrollTo(0, desde + (meta() - desde) * e2);
      if (p < 1) requestAnimationFrame(paso);
      else if (lenis) lenis.start();
    };
    requestAnimationFrame(paso);
  });

  // AVISO EN DESARROLLO: un renglón del titular que no cabe.
  // Los cortes del titular se eligen a mano, así que `nowrap` impide que se
  // parta solo —pero entonces desborda, y desbordar en silencio es igual de
  // malo—. Esto lo dice. Pasó al cambiar la frase a "De ingeniero a gerente":
  // el renglón largo pedía 893px contra los 888 de la columna y el titular
  // salía en tres líneas en vez de dos.
  // Solo en desarrollo: en producción no sirve de nada y cuesta un reflow.
  if (import.meta.env?.DEV) {
    requestAnimationFrame(() => {
      const caja = copy.clientWidth - 2 * parseFloat(getComputedStyle(copy).paddingLeft);
      host.querySelectorAll('.zk-hj__h1 span').forEach((s) => {
        const w = s.scrollWidth;
        if (w > caja + 1) {
          console.warn(`[zenkai] el renglón "${s.textContent}" del titular pide ${Math.round(w)}px y la columna da ${Math.round(caja)}: baja el término vw de .zk-hj__h1 o acorta el corte.`);
        }
      });
    });
  }

  stage.classList.add('is-covered');   // arranca tapado; paint() lo actualiza
  let lastP = -1, first = true, visible = true;
  const tick = () => {
    if (visible) {
      const rb = rail.getBoundingClientRect();
      const target = clamp(-rb.top / leadPx);
      if (first) { p = target; first = false; } else p += (target - p) * 0.14;
      if (Math.abs(p - lastP) > 0.0004) { lastP = p; paint(); }
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { rootMargin: '200px 0px' }).observe(stage);
  }

  return host;
}
